import React, { ReactElement, useMemo } from "react";
import {
    MetricsDashboardDataColumn,
    MetricsDashboardGraphDataSource,
    useGetReportFieldsQuery,
} from "../../../__generated__/api";
import Select from "../../select/Select";

export interface ColumnSetGroup {
    id: string;
    label: string;
    description: string;
}

const DEFAULT_SYSTEM_GROUP: ColumnSetGroup = {
    id: "urn:fq:form-field:system-fields",
    description: "All custom fields not included in the ones above.",
    label: "System Fields",
};

const DEFAULT_CUSTOM_GROUP: ColumnSetGroup = {
    id: "urn:fq:form-field:custom-fields",
    description: "All custom fields not included in the ones above.",
    label: "Custom Fields",
};

export interface ColumnSetValue {
    group: ColumnSetGroup;
    columns: MetricsDashboardDataColumn[];
}

export interface ColumnSet {
    dataSource: MetricsDashboardGraphDataSource;
    columns: ColumnSetValue[];
}

export function useFlatFields(
    dataSource: MetricsDashboardGraphDataSource,
) {
    const { data: formData } = useGetReportFieldsQuery();

    return useMemo(() => {
        const columns = formData?.metricsDashboardFields?.columns.find(set => set.dataSource === dataSource)?.columns ?? [];

        const result: ColumnSet = {
            dataSource,
            columns: [],
        };
        if (dataSource === MetricsDashboardGraphDataSource.Tickets) {
            result.columns.push({
                group: DEFAULT_SYSTEM_GROUP,
                columns: columns.filter(row => row.field.isSystemField),
            });
            result.columns.push({
                group: DEFAULT_CUSTOM_GROUP,
                columns: columns.filter(row => !row.field.isSystemField),
            });
        } else {
            result.columns.push({
                group: DEFAULT_SYSTEM_GROUP,
                columns,
            });
        }
        const flatFields = result.columns.flatMap(group => group.columns?.map(column => ({
            id: column.id,
            column,
            group,
        }))).filterNull().toIdMap("id");

        return {
            columns: result,
            flatFields,
        };
    }, [dataSource, formData]);
}
export default function ReportFieldSelector({
    dataSource, value, onChange, isClearable,
}: {
    dataSource: MetricsDashboardGraphDataSource,
    value: MetricsDashboardDataColumn | string | undefined;
    onChange: (value: MetricsDashboardDataColumn | undefined) => void;
    isClearable?: boolean;
}): ReactElement {
    const { flatFields, columns } = useFlatFields(dataSource);

    const typedValue = typeof value === "string" ? flatFields?.[value]?.column : value;
    return (
        <Select
            isClearable={isClearable}
            onChange={data => onChange(data?.data)}
            value={!typedValue ? undefined : {
                label: typedValue.field.name,
                value: typedValue.field.id,
                data: typedValue,
            }}
            options={columns.columns.map(row => ({
                label: row.group.label,
                value: row.group.id,
                options: row.columns?.map(field => ({
                    label: field.field.name,
                    value: field.id,
                    data: field,
                })),
            })) ?? []}
        />
    );
}

ReportFieldSelector.defaultProps = {
    isClearable: false,
};
